<template>
  <v-card>
    <v-card-title>
      {{ title }}
      <v-spacer />
    </v-card-title>
    <v-divider />
    <v-data-table
      :loading="loading"
      :headers="headers"
      :items="items"
      disable-pagination
      hide-default-footer
      mobile-breakpoint="0"
      :show-expand="type == 'materials' || type == 'stocks' || type == 'finishes'"
    >
      <template #[`item.providerId`]="{ item }">
        {{ provider_name(item.providerId) }}
      </template>
      <template #[`item.displayName`]="{ item }">
        <v-text-field
          v-model="item.displayName"
          outlined
          dense
          hide-details="auto"
          @input="item.changed = true"
        >
          <template #append-outer>
            <v-btn
              v-show="item.changed"
              color="success"
              @click="save('displayName', item)"
            >
              Save
            </v-btn>
          </template>
        </v-text-field>
      </template>
      <template #[`item.gsm`]="{ item }">
        <v-text-field
          v-model="item.gsm"
          :rules="[$rules.int]"
          outlined
          dense
          hide-details="auto"
          @input="item.changed = true"
        >
          <template #append-outer>
            <v-btn
              v-show="item.changed"
              color="success"
              @click="save('gsm', item)"
            >
              Save
            </v-btn>
          </template>
        </v-text-field>
      </template>
      <template #[`item.description`]="{ item }">
        <v-textarea
          v-model="item.description"
          outlined
          dense
          hide-details="auto"
          @input="item.changed = true"
        >
          <template #append-outer>
            <v-btn
              v-show="item.changed"
              color="success"
              @click="save('description', item)"
            >
              Save
            </v-btn>
          </template>
        </v-textarea>
      </template>
      <template #[`item.providers`]="{ item }">
        <div
          v-for="(provider, i) in item.providers"
          :key="i"
        >
          <span class="font-weight-bold"> {{ provider_name(provider.providerId) }}: </span>
          {{ provider.externalId }}
        </div>
      </template>
      <template #[`item.imageUrl`]="{ item }">
        <v-icon :color="item.imageUrl ? 'success' : 'error'">
          {{ item.imageUrl ? 'check_circle' : 'cancel' }}
        </v-icon>
      </template>
      <template #[`item.sortOrder`]="{ item }">
        <v-text-field
          v-model="item.sortOrder"
          outlined
          dense
          hide-details="auto"
          type="number"
          @input="item.changed = true"
        >
          <template #append-outer>
            <v-btn
              v-show="item.changed"
              color="success"
              @click="save('sortOrder', item)"
            >
              Save
            </v-btn>
          </template>
        </v-text-field>
      </template>
      <template #[`item.days`]="{ item }">
        <v-text-field
          v-model="item.days"
          outlined
          dense
          hide-details="auto"
          @input="item.changedDays = true"
        >
          <template #append-outer>
            <v-btn
              v-show="item.changedDays"
              color="success"
              @click="save('days', item)"
            >
              Save
            </v-btn>
          </template>
        </v-text-field>
      </template>
      <template #[`item.actions`]="{}" />

      <template #expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <v-card
            class="my-2"
            style="max-width: 400px"
          >
            <v-card-title>Image</v-card-title>
            <v-card-text>
              <ImageUpload
                height="200"
                :url="item.imageUrl"
                @save="saveImage(item.id, $event)"
              />
            </v-card-text>
          </v-card>
          <MaterialTraits
            class="mb-2"
            :material="item"
            is-base
          />
        </td>
      </template>
    </v-data-table>
    <LoadingSpinnerDialog :loading="loading_dialog" />
  </v-card>
</template>

<script>
import ImageUpload from '@/components/common/ImageUpload';
import MaterialTraits from '@/components/print/MaterialTraits';
import { mapGetters } from 'vuex';
export default {
  components: { MaterialTraits, ImageUpload },
  props: {
    type: { type: String, default: '' },
    title: { type: String, default: '' }
  },
  data: () => ({
    loading: true,
    loading_dialog: false,
    groupId: null,
    saving: false
  }),
  computed: {
    headers() {
      const items = [{ text: 'Name', value: 'name' }];

      if (this.type !== 'turnaround_types') {
        items.push({ text: 'Display Name', value: 'displayName' });
      }

      if (this.type === 'stocks') {
        items.push({ text: 'Gsm', value: 'gsm' });
      }

      if (this.type === 'materials') {
        items.push({
          text: 'Base Traits',
          value: 'baseTraits'
        });
      }
      if (this.type === 'materials' || this.type === 'stocks' || this.type === 'finishes') {
        items.push({ text: 'Image?', value: 'imageUrl' });
      }

      if (this.type === 'turnaround_types') {
        items.push({ text: 'Provider', value: 'providerId' });
      } else if (this.type === 'stocks' || this.type === 'finishes') {
        items.push({ text: 'Providers', value: 'providers' });
      }

      if (this.type === 'turnaround_types') {
        items.push({ text: 'Days', value: 'days' });
      }
      if (this.type !== 'materials' && this.type !== 'stocks' && this.type !== 'finishes') {
        items.push({ text: 'Ext. Id', value: 'externalId' });
      }
      if (this.type === 'option_groups') {
        items.push({ text: 'Desc', value: 'description' });
      }
      items.push({ text: '', value: 'actions' });
      return items;
    },
    items() {
      let items = this.lookups[this.type];
      if (!items) {
        return [];
      }

      if (this.type === 'materials') {
        items = items.map((x) =>
          Object.assign(x, {
            baseTraits: x.traits.filter((y) => !y.categoryId).length
          })
        );
      }

      return items.map((x) => Object.assign(x, { changed: false, changedDays: false }));
    },
    ...mapGetters({
      provider_name: 'lookup/print_provider_name',
      lookups: 'print/lookups',
      category_name: 'lookup/category_name'
    })
  },
  created() {
    this.loadData();
  },
  methods: {
    loadData() {
      this.loading = true;
      this.$store
        .dispatch('print/getLookups')
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    save(field, item) {
      this.loading = true;
      this.$store
        .dispatch('print/override', {
          type: this.type,
          field: field,
          id: item.id,
          value: item[field]
        })
        .then(() => {
          item.changed = false;
          item.changedDays = false;
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async saveImage(id, e) {
      this.saving = true;
      this.$store
        .dispatch('print/savePrintImage', {
          id,
          type: this.type,
          file: e.target.files[0]
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    addTrait(materialId) {
      this.$refs.addTrait.showDialog(materialId);
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .v-input__append-outer {
  margin-top: 0 !important;
}
</style>
